import React, { useMemo } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import { TextField, Tooltip, Typography } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { useAppDispatch, useUser } from '../../../hooks'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router'
import { setOrganization, setWorkSpaceUser } from '../../../store'
import { Routes } from '../../../main/config/routes'
import { isAccountManager } from '../../../helpers'

type OrganizationOption = {
    id: number | null
    name: string
}

const ALL_ORGANIZATIONS: OrganizationOption = { id: null, name: 'All' }

const WorkSpaceSelection = () => {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            container: {
                display: 'flex',
                alignItems: 'stretch',
            },
            workspaceCount: {
                display: 'flex',
                alignItems: 'center',
                height: '56px',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
                borderRight: '1px solid rgba(255, 255, 255, 0.4)',
                marginRight: theme.spacing(2),
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                fontWeight: 500,
            },
            workspaceDropdownWrapper: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '56px',
                width: 252,
            },
            workspaceDropdown: {
                width: 252,
                color: '#FFF',
                height: '56px',
                '& .MuiFormControl-root, .MuiInputBase-root': {
                    height: '100%',
                    color: '#FFF',
                },
                '& .MuiInputBase-root': {
                    paddingLeft: '12px',
                },
                '& .MuiButtonBase-root': {
                    color: '#FFF',
                },
                '& .MuiInput-underline:before, .MuiInput-underline:after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 0,
                },
            },
            workspaceDropdownWithEzid: {
                width: 252,
                color: '#FFF',
                height: 'auto',
                '& .MuiFormControl-root, .MuiInputBase-root': {
                    height: 'auto',
                    color: '#FFF',
                },
                '& .MuiInputBase-root': {
                    paddingLeft: '12px',
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                '& .MuiInputBase-input': {
                    paddingTop: '2px',
                    paddingBottom: '0px',
                },
                '& .MuiButtonBase-root': {
                    color: '#FFF',
                },
                '& .MuiInput-underline:before, .MuiInput-underline:after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 0,
                },
            },
            ezidUnderName: {
                fontSize: '0.7rem',
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.75)',
                paddingLeft: '12px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
            },
            optionLabel: {
                display: 'flex',
                flexDirection: 'column',
            },
            optionEzid: {
                fontSize: '0.7rem',
                color: 'rgba(0, 0, 0, 0.54)',
                lineHeight: 1.2,
            },
            organizationDropdown: {
                width: 252,
                color: '#FFF',
                borderLeft: '2px solid #FFF',
                height: '56px',
                '& .MuiFormControl-root, .MuiInputBase-root': {
                    height: '100%',
                    color: '#FFF',
                },
                '& .MuiInputBase-root': {
                    paddingLeft: '12px',
                },
                '& .MuiButtonBase-root': {
                    color: '#FFF',
                },
                '& .MuiInput-underline:before, .MuiInput-underline:after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 0,
                },
            },
        }),
    )
    const classes = useStyles()
    const history = useHistory()
    const dispatch = useAppDispatch()

    const { workspaceUser, rootUser, organization } = useUser()
    const selectedOrganizationFilter = organization ?? null

    const isAccountManagerUser = rootUser ? isAccountManager(rootUser) : false

    const organizationList: OrganizationOption[] = useMemo(() => {
        const list: OrganizationOption[] = [ALL_ORGANIZATIONS]
        if (!rootUser?.workspaces) return list
        rootUser.workspaces.forEach((workspace) => {
            const organizationId = Number(
                workspace.active_workspace.organization,
            )
            const organizationName =
                workspace.active_workspace.organization_name
            const existing = list.find((org) => org.id === organizationId)
            if (!existing) {
                list.push({ id: organizationId, name: organizationName })
            }
        })
        return list
    }, [rootUser?.workspaces])

    const filteredWorkspaces = useMemo(() => {
        const workspaces = rootUser?.workspaces ?? []
        if (!isAccountManagerUser || !selectedOrganizationFilter)
            return workspaces
        const orgId = Number(selectedOrganizationFilter)
        return workspaces.filter(
            (ws) => Number(ws.active_workspace.organization) === orgId,
        )
    }, [rootUser?.workspaces, selectedOrganizationFilter, isAccountManagerUser])

    const selectedOrganizationOption =
        organizationList.find((org) => org.id === selectedOrganizationFilter) ??
        ALL_ORGANIZATIONS

    const handleOrganizationChange = (
        _event: React.ChangeEvent<{}>,
        newValue: OrganizationOption | null,
    ) => {
        if (!newValue) return
        if (newValue.id !== null && rootUser?.workspaces) {
            const orgId = Number(newValue.id)
            const firstWorkspace = rootUser.workspaces
                .filter((ws) => ws.active_workspace.company_type !== 'VENDOR')
                .find(
                    (ws) => Number(ws.active_workspace?.organization) === orgId,
                )
            if (firstWorkspace) {
                if (firstWorkspace.active_workspace.company_type !== 'VENDOR') {
                    history.push(Routes.apartmentSchedule)
                } else {
                    history.push(Routes.ezwork)
                }
            }
        }
        dispatch(setOrganization(newValue.id))
    }

    const handleWorkspaceChange = (
        _event: React.ChangeEvent<{}>,
        newValue: any,
    ) => {
        const newWsUser = rootUser?.workspaces.find(
            (wsUser) => wsUser.id === newValue.id,
        )
        if (newWsUser) {
            if (newWsUser?.active_workspace.company_type !== 'VENDOR') {
                history.push(Routes.apartmentSchedule)
            } else {
                history.push(Routes.ezwork)
            }
            dispatch(setWorkSpaceUser(newWsUser))
            toast.success(`Switched to ${newWsUser.active_workspace.name}`)
        } else {
            toast.error('Workspace not found')
        }
    }

    const workspaceCount = filteredWorkspaces.length
    const showWorkspaceCount = workspaceCount > 1
    const isVendorWorkspace =
        workspaceUser?.active_workspace.company_type === 'VENDOR'

    // Don't render Autocomplete until we have a valid workspaceUser and options (avoids MUI throw when value is undefined)
    if (!workspaceUser || !rootUser?.workspaces?.length) {
        return (
            <div className={classes.container}>
                <Typography
                    variant="body2"
                    className={classes.workspaceCount}
                    component="span"
                >
                    —
                </Typography>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            {showWorkspaceCount && (
                <Tooltip title={`${workspaceCount} workspaces available`}>
                    <Typography
                        variant="body2"
                        className={classes.workspaceCount}
                        component="span"
                    >
                        {workspaceCount}
                    </Typography>
                </Tooltip>
            )}
            <div className={classes.workspaceDropdownWrapper}>
                <Autocomplete
                    disableClearable
                    id="workspace-combo-box"
                    options={filteredWorkspaces}
                    getOptionLabel={(option) =>
                        option.active_workspace
                            ? option.active_workspace.name
                            : ''
                    }
                    renderOption={(option) => (
                        <div className={classes.optionLabel}>
                            <span>
                                {option.active_workspace
                                    ? option.active_workspace.name
                                    : ''}
                            </span>
                            {option.active_workspace?.company_type ===
                                'VENDOR' && (
                                <span className={classes.optionEzid}>
                                    EZID {option.active_workspace.id}
                                </span>
                            )}
                        </div>
                    )}
                    className={
                        isVendorWorkspace
                            ? classes.workspaceDropdownWithEzid
                            : classes.workspaceDropdown
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            style={{ color: '#FFF' }}
                            placeholder="Search"
                        />
                    )}
                    value={workspaceUser}
                    onChange={handleWorkspaceChange}
                    style={{ zIndex: Number.MAX_SAFE_INTEGER }}
                />
                {isVendorWorkspace && (
                    <span className={classes.ezidUnderName}>
                        EZID {workspaceUser.active_workspace.id}
                    </span>
                )}
            </div>
            {isAccountManagerUser && (
                <Autocomplete
                    disableClearable
                    id="organization-combo-box"
                    options={organizationList}
                    getOptionLabel={(option) => option.name}
                    className={classes.organizationDropdown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            style={{ color: '#FFF' }}
                            placeholder="Organization"
                        />
                    )}
                    value={selectedOrganizationOption}
                    onChange={handleOrganizationChange}
                    style={{ zIndex: Number.MAX_SAFE_INTEGER }}
                />
            )}
        </div>
    )
}

export default WorkSpaceSelection
